/**
 * Central query-key factory. Hooks and cache mutations both read keys
 * from here so cache invalidations never drift away from fetches.
 *
 * Shape:
 *
 *   queryKeys.invoices.list(orgId)         // ['invoices', orgId]
 *   queryKeys.invoices.detail(orgId, id)   // ['invoices', orgId, id]
 *   queryKeys.invoices.activities(orgId,id)// ['invoices', orgId, id, 'activities']
 *
 * Helper matchers (`.all(orgId)`, `.byOrg(orgId)`) exist for invalidating
 * a subtree in one call without re-stating the key prefix.
 */

type Key = readonly (string | number)[];

export const queryKeys = {
  organizations: {
    all: () => ['organizations'] as const,
    archived: () => ['organizations', 'archived'] as const,
    detail: (id: string) => ['organizations', id] as const,
    settings: (id: string) => ['organizations', id, 'settings'] as const,
  },
  session: {
    current: () => ['session'] as const,
  },
  clients: {
    all: (orgId: string) => ['clients', orgId] as const,
    detail: (orgId: string, clientId: string) =>
      ['clients', orgId, clientId] as const,
    activities: (orgId: string, clientId: string) =>
      ['clients', orgId, clientId, 'activities'] as const,
  },
  invoices: {
    all: (orgId: string) => ['invoices', orgId] as const,
    detail: (orgId: string, invoiceId: string) =>
      ['invoices', orgId, invoiceId] as const,
    activities: (orgId: string, invoiceId: string) =>
      ['invoices', orgId, invoiceId, 'activities'] as const,
    /** Peek at the next-to-be-issued invoice number (does not bump the seq). */
    peekNumber: (orgId: string) => ['invoices', orgId, 'next-number'] as const,
  },
  items: {
    all: (orgId: string) => ['items', orgId] as const,
    detail: (orgId: string, itemId: string) =>
      ['items', orgId, itemId] as const,
    activities: (orgId: string, itemId: string) =>
      ['items', orgId, itemId, 'activities'] as const,
  },
  taxOptions: {
    /**
     * Tax options now scope by org (the v0.1 server stores per-org
     * libraries, not a global one). Two factories so old callers
     * that pass no orgId can still produce a stable invalidation
     * prefix during the cleanup window in stage 7.
     */
    byOrg: (orgId: string) => ['tax-options', orgId] as const,
    detail: (orgId: string, id: string) => ['tax-options', orgId, id] as const,
    /** @deprecated use `byOrg(orgId)` instead. */
    all: () => ['tax-options'] as const,
  },
  paymentProcessors: {
    stripe: (orgId: string) => ['payment-processors', 'stripe', orgId] as const,
  },
  payments: {
    byInvoice: (orgId: string, invoiceId: string) =>
      ['payments', 'invoice', orgId, invoiceId] as const,
    summary: (orgId: string, invoiceId: string) =>
      ['payments', 'summary', orgId, invoiceId] as const,
  },
  paymentMethods: {
    byOrg: (orgId: string) => ['payment-methods', orgId] as const,
    customInstructions: (orgId: string) =>
      ['payment-methods', orgId, 'custom-instructions'] as const,
  },
  memberships: {
    list: (orgId: string) => ['memberships', orgId] as const,
  },
  invites: {
    list: (orgId: string) => ['invites', orgId] as const,
    /** Public invite-preview by token; org-agnostic so it sits at the root. */
    preview: (token: string) => ['invites', 'preview', token] as const,
  },
  user: {
    me: () => ['user', 'me'] as const,
    profile: (userId: string) => ['user', userId, 'profile'] as const,
    security: (userId: string) => ['user', userId, 'security'] as const,
    /**
     * Read-only preflight for the delete-account flow. Lives under
     * `me` because there's no notion of inspecting another user's
     * delete shape — the data is always "what would happen if I,
     * the caller, hit DELETE /users/me right now".
     */
    deletePreflight: () => ['user', 'me', 'delete-preflight'] as const,
    /**
     * Mobile push-token registry. Single per-user key — every
     * device's token UPSERTs through the same endpoint, so cache
     * invalidation operates on the entire list rather than per
     * token.
     */
    pushTokens: () => ['user', 'me', 'push-tokens'] as const,
  },
  subscription: {
    plans: () => ['subscription', 'plans'] as const,
    forUser: (userId: string) => ['subscription', userId] as const,
  },
  templates: {
    all: (orgId?: string) =>
      orgId ? (['invoice-templates', orgId] as const) : (['invoice-templates'] as const),
    detail: (templateId: string) => ['invoice-templates', templateId] as const,
  },
  appDefaults: {
    byOrg: (orgId: string) => ['app-defaults', orgId] as const,
    supportedCurrencies: () => ['app-defaults', 'currencies'] as const,
    businessTypes: () => ['app-defaults', 'business-types'] as const,
  },
  feedback: {
    categories: () => ['feedback', 'categories'] as const,
    forUser: (userId: string) => ['feedback', 'user', userId] as const,
  },
  notifications: {
    list: () => ['notifications'] as const,
    unreadCount: () => ['notifications', 'unread-count'] as const,
  },
  printer: {
    /**
     * Latest cached preview for the invoice. Resolved by the
     * `/preview` endpoint server-side and reflected by the client
     * hook so refetches reuse the existing entry until the invoice
     * mutates.
     */
    preview: (orgId: string, invoiceId: string) =>
      ['printer', 'preview', orgId, invoiceId] as const,
    /** Status of an in-flight render job, polled by `useRenderInvoice`. */
    job: (orgId: string, invoiceId: string, jobId: string) =>
      ['printer', 'job', orgId, invoiceId, jobId] as const,
  },
  support: {
    helpCategories: () => ['support', 'help-categories'] as const,
    helpArticles: (categoryId?: string) =>
      categoryId
        ? (['support', 'help-articles', categoryId] as const)
        : (['support', 'help-articles'] as const),
    helpArticle: (articleId: string) =>
      ['support', 'help-article', articleId] as const,
    faqs: (categoryId?: string) =>
      categoryId ? (['support', 'faqs', categoryId] as const) : (['support', 'faqs'] as const),
    search: (query: string) => ['support', 'search', query] as const,
  },
} as const;

export type QueryKey = Key;

/**
 * Utility for invalidating an entire subtree. Example:
 *
 *   queryClient.invalidateQueries({ queryKey: keyPrefix(['invoices', orgId]) })
 *
 * We expose this so consumers can invalidate by partial prefix without
 * stringly-typing the subset.
 */
export function keyPrefix<K extends Key>(key: K): K {
  return key;
}
